import type { Language } from '@eformulieren/engine'
import { type AdviesAnkerSoort, type AdviesToestand, type BelangPartijen, isOpenAdvies } from '@eformulieren/model'
import type { Bilingual } from '../language/text.js'

/**
 * Wat het adviespaneel en de adviseurpagina tonen, zonder DOM (R-184, R-184b, R-184c, ontwerp 7.4a,
 * mockups 01, 05 en 06 van advies).
 *
 * **Het scherm krijgt alleen tekst om te tonen.** De pagina zet elke persoon om in wat de kijker mag
 * lezen ("U", het adres dat de pod meestuurt, "Adviseur binnen NWO", "NWO"); een persoonssleutel komt
 * hier niet binnen (`apps/runtime/pagina/advice.ts`). Het anker draagt de ids van sectie en veld om
 * erheen te springen en een label in de taal van de lezer.
 */

/** Een plek in het formulier: een sectie, of een vraag in een sectie. */
export interface AdviceTarget {
  readonly sectieId: string
  readonly veldId?: string
}

export interface AdviceAnchorView {
  readonly soort: AdviesAnkerSoort
  readonly sectieId: string | null
  readonly veldId: string | null
  /** De naam van de vraag of sectie in de taal van de lezer, of de naam van het formulier. */
  readonly label: string
}

/** Eén reactie in het gesprek, met wie schreef als tekst. */
export interface AdviceReactionView {
  readonly id: string
  readonly wie: string
  readonly rol: 'vrager' | 'adviseur'
  readonly tekst: string
  readonly moment: string
}

/** Een adviesverzoek zoals het paneel het toont. */
export interface AdviceRequestView {
  readonly id: string
  readonly toestand: AdviesToestand
  readonly anker: AdviceAnchorView
  /** De adviseur zoals de kijker hem mag zien: het adres als de pod het meestuurt, anders zijn rol. */
  readonly adviseur: string
  /** Een datum als `2026-12-12`, of niets. */
  readonly termijn: string | null
  /** Oudste eerst; de eerste is de vraag. */
  readonly reacties: readonly AdviceReactionView[]
  /** Bij een gemeld belang: wat de adviseur erbij schreef, als hij iets schreef. */
  readonly belangToelichting?: string
  /** De belangenverklaring van de adviseur, zodra die er is (R-184d, R-184j); voor de kaart van de vrager. */
  readonly verklaring?: AdviceInterestView
  /** De oordelen van NWO over die verklaring, oudste eerst; het laatste geldt (R-184k). */
  readonly oordelen?: readonly AdviceJudgementView[]
  /** Of de kijker het oordeel van NWO kan geven (`kanBeoordelen` van de pod). */
  readonly kanBeoordelen?: boolean
}

/** De verklaring zoals de vrager haar leest: de keuze, de toelichting, wanneer, en wat de adviseur zag. */
export interface AdviceInterestView {
  readonly uitkomst: 'geen-belang' | 'belang'
  readonly toelichting: string | null
  readonly moment: string
  readonly partijen: BelangPartijen | null
}

/** Eén oordeel van NWO, met wie als tekst ("U", "Medewerker van NWO", "NWO"). */
export interface AdviceJudgementView {
  readonly uitkomst: 'geen-belang' | 'belang'
  readonly toelichting: string
  readonly wie: string
  readonly moment: string
}

export type AdviceFilter = 'alles' | 'open' | 'sectie'

/** Waar het anker in het formulier staat, als het daar staat. */
export function targetOf(anchor: AdviceAnchorView): AdviceTarget | undefined {
  if (anchor.sectieId === null) return undefined
  return anchor.veldId === null ? { sectieId: anchor.sectieId } : { sectieId: anchor.sectieId, veldId: anchor.veldId }
}

/**
 * De verzoeken onder een filter, in de volgorde van de pod. "Deze sectie" is de sectie waar de lezer
 * nu is: een vraag erin of de sectie zelf. Het formulier als geheel hoort bij geen sectie.
 */
export function filterAdvice(requests: readonly AdviceRequestView[], filter: AdviceFilter, activeSection: string | undefined): readonly AdviceRequestView[] {
  switch (filter) {
    case 'alles':
      return requests
    case 'open':
      return requests.filter((request) => isOpenAdvies(request.toestand))
    case 'sectie':
      return activeSection === undefined ? [] : requests.filter((request) => request.anker.sectieId === activeSection)
  }
}

export function filterCounts(requests: readonly AdviceRequestView[], activeSection: string | undefined): Readonly<Record<AdviceFilter, number>> {
  return {
    alles: requests.length,
    open: filterAdvice(requests, 'open', activeSection).length,
    sectie: filterAdvice(requests, 'sectie', activeSection).length,
  }
}

/**
 * Het aantal verzoeken met precies deze plek als anker, ook als ze zijn geëindigd: het label bij een
 * vraag of sectie zegt dat er over gesproken is. Een sectie telt alleen wat aan haarzelf hangt; een
 * vraag erin heeft zijn eigen label. Een criterium is een veld in een toetssectie.
 */
export function adviceCountFor(requests: readonly AdviceRequestView[], target: AdviceTarget): number {
  return requests.filter((request) => {
    const anchor = request.anker
    if (target.veldId !== undefined) return anchor.veldId === target.veldId && anchor.sectieId === target.sectieId
    return anchor.soort === 'sectie' && anchor.sectieId === target.sectieId
  }).length
}

/** De verzoeken bij precies deze plek, zoals {@link adviceCountFor} ze telt. */
export function adviceAt(requests: readonly AdviceRequestView[], target: AdviceTarget): readonly AdviceRequestView[] {
  return requests.filter((request) => adviceCountFor([request], target) === 1)
}

export function adviceCountWord(count: number, language: Language): string {
  if (language === 'en') return count === 1 ? '1 request for advice' : `${String(count)} requests for advice`
  return count === 1 ? '1 advies' : `${String(count)} adviezen`
}

const FORM_AS_WHOLE: Bilingual = { nl: 'Formulier als geheel', en: 'The form as a whole' }
const QUESTION: Bilingual = { nl: 'Vraag: {a}', en: 'Question: {a}' }
const SECTION: Bilingual = { nl: 'Sectie: {a}', en: 'Section: {a}' }

/** De naam van het anker op een kaart: "Vraag: Begroting", "Sectie: Onderzoeksopzet", "Formulier als geheel". */
export function adviceAnchorTitle(anchor: AdviceAnchorView, language: Language): string {
  switch (anchor.soort) {
    case 'formulier':
      return FORM_AS_WHOLE[language]
    case 'sectie':
      return SECTION[language].replace('{a}', anchor.label)
    case 'veld':
    case 'criterium':
      return QUESTION[language].replace('{a}', anchor.label)
  }
}

const NEW_ADVICE: Bilingual = { nl: 'Nieuw advies van {w} over {a}.', en: 'New advice from {w} about {a}.' }
const INTEREST: Bilingual = {
  nl: '{w} heeft een belang gemeld bij {a}. Het verzoek is beëindigd.',
  en: '{w} has declared an interest regarding {a}. The request has ended.',
}
const LAPSED: Bilingual = { nl: 'De adviesvraag over {a} is vervallen.', en: 'The request for advice about {a} has lapsed.' }

/**
 * De zinnen voor de live regio (`aria-live=polite`): wat er sinds de vorige lezing bij is gekomen.
 * Een antwoord van de adviseur, een gemeld belang en een vervallen verzoek; wat de lezer zelf deed
 * (een vervolgvraag, afronden, intrekken) niet, want dat weet hij al.
 */
export function adviceNews(before: readonly AdviceRequestView[], after: readonly AdviceRequestView[], language: Language): readonly string[] {
  const previous = new Map(before.map((request) => [request.id, request]))
  const news: string[] = []
  for (const request of after) {
    const old = previous.get(request.id)
    const about = adviceAnchorTitle(request.anker, language)
    const fill = (text: Bilingual) => text[language].replace('{w}', request.adviseur).replace('{a}', about)
    const known = new Set(old?.reacties.map((reaction) => reaction.id) ?? [])
    if (request.reacties.some((reaction) => reaction.rol === 'adviseur' && !known.has(reaction.id))) news.push(fill(NEW_ADVICE))
    if (old !== undefined && old.toestand !== request.toestand) {
      if (request.toestand === 'belang-gemeld') news.push(fill(INTEREST))
      if (request.toestand === 'vervallen') news.push(fill(LAPSED))
    }
  }
  return news
}
